import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { Divider, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import NodeSelection from '../NodeSelection';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  SendToDivisionCommandInfo,
  SendToLanesCommandInfo,
  SendToNodeGroupsCommandInfo,
  SendToSiteNumbersCommandInfo,
} from '../../Interfaces/Commands/CommandInfo';
import {
  sendToDivisionCommand,
  sendToLanesCommand,
  sendToNodeGroupsCommand,
  sendToSiteNumberCommand,
} from '../../store/Assets/AssetsActions';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';

import { convertDateYYYYMMDD, maskString } from '../../utils/utils';
import {
  DateRangePicker,
  DateRangePickerChangeEvent,
  SelectionRange,
} from '@progress/kendo-react-dateinputs';
import { LogsFolderInfo } from '../../Interfaces/LogsInfo/LogsFolderInfo';
import {
  InputPrefix,
  InputSeparator,
  InputSuffix,
  TextBox,
} from '@progress/kendo-react-inputs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button } from '@progress/kendo-react-buttons';
import clsx from 'clsx';
import CommandHistory from '../CommandsHistory/CommandHistory';
import { getDateCreatedCommandByBatchList } from '../../store/Commands/CommandsAction';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { LogsFilterCell } from './LogsFilterCell';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logsContainer: {
      display: 'flex',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
    span: {
      fontWeight: 'bold',
      fontSize: '1rem',
      margin: '10px',
    },
    margin: {
      margin: theme.spacing(1),
    },
    copyToClipBoard: {
      fontSize: '1.5rem',
    },
    copyToClipBoardGrid: { marginTop: 22, marginLeft: 20 },
    buttonStyle: { marginLeft: '10px' },
    dateRangePickerGrid: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '10px',
    },
  }),
);

const LogsContainer: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const commandName: string = 'UploadLogs';

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const [commandSend, displayCommandSend] = React.useState(false);
  const [historyStartDate, setHistoryStartDate] = React.useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [historyEndDate, setHistoryEndDate] = React.useState<Date>(new Date());
  const [useCurrentDate, setUseCurrentDate] = React.useState<boolean>(true);

  const logsUploadPath: LogsFolderInfo = useSelector(
    (state: ApplicationState) => state.logs.folderInfo,
  );

  const [dateRangeValue, setDateRangeValue] = React.useState<SelectionRange>({
    start: new Date(),
    end: new Date(),
  });

  const getParameters = (startDate: any, endDate: any) => {
    return `<UploadLogs><Path>${
      logsUploadPath.Path
    }</Path><StartDate>${convertDateYYYYMMDD(
      startDate,
    )}</StartDate><EndDate>${convertDateYYYYMMDD(
      endDate,
    )}</EndDate></UploadLogs>`;
  };

  const setSendToSiteNumberCommand = (
    command: SendToSiteNumbersCommandInfo,
  ) => {
    let commandInfo: SendToSiteNumbersCommandInfo = {
      Division: command.Division,
      SiteNumbers: command.SiteNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: getParameters(dateRangeValue?.start, dateRangeValue?.end),
      },
    };
    sendToSiteNumberCommand(dispatch, commandInfo);

    displayCommandSend(true);
  };

  const setSendToNodeGroupsCommand = (command: SendToNodeGroupsCommandInfo) => {
    let commandInfo: SendToNodeGroupsCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroups: command.NodeGroups,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: getParameters(dateRangeValue?.start, dateRangeValue?.end),
      },
    };

    sendToNodeGroupsCommand(dispatch, commandInfo);

    displayCommandSend(true);
  };

  const setSendToLanesCommand = (command: SendToLanesCommandInfo) => {
    let commandInfo: SendToLanesCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroup: command.NodeGroup,
      LaneNumbers: command.LaneNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: getParameters(dateRangeValue?.start, dateRangeValue?.end),
      },
    };

    sendToLanesCommand(dispatch, commandInfo);

    displayCommandSend(true);
  };

  const setSendToDivisionCommand = (command: SendToDivisionCommandInfo) => {
    let commandInfo: SendToDivisionCommandInfo = {
      Divisions: command.Divisions,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: getParameters(dateRangeValue?.start, dateRangeValue?.end),
      },
    };

    sendToDivisionCommand(dispatch, commandInfo);

    displayCommandSend(true);
  };

  const onHistoryStartDateChange = (date: Date) => {
    setHistoryStartDate(date);
  };

  const onHistoryEndDateChange = (date: Date) => {
    setHistoryEndDate(date);
  };

  const onUseCurrentDateTimeFilterChange = (useCurrentDate: boolean) => {
    setUseCurrentDate(useCurrentDate);
  };

  useEffect(() => {
    let timeoutId: any;
    if (commandSend) {
      timeoutId = setTimeout(() => {
        getDateCreatedCommandByBatchList(
          dispatch,
          commandName,
          historyStartDate.toUTCString(),
          useCurrentDate
            ? new Date().toUTCString()
            : historyEndDate.toUTCString(),
        );
        displayCommandSend(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandSend]);

  const customStartDateInputSettings = {
    label: GetString('START_DATE_LABEL'),
  };

  const customEndDateInputSettings = {
    label: GetString('END_DATE_LABEL'),
  };

  var maxDateRange = new Date();

  const dateRangeOnChange = (event: DateRangePickerChangeEvent) => {
    setDateRangeValue(event.value);
  };

  const [showCopyToClipBoard, setShowCopyToClipBoard] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    let timeoutId: any;
    if (showCopyToClipBoard) {
      timeoutId = setTimeout(() => {
        setShowCopyToClipBoard(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showCopyToClipBoard]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Grid className={classes.margin}>
            <StringResourceManager
              variant="body1"
              style="commandLabel"
              stringKey="UPLOAD_LOGS"
              gutterBottom={true}
            />
          </Grid>
          <Grid className={clsx(classes.logsContainer, classes.margin)}>
            <Grid>
              <Grid>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'DATE_RANGE'}
                  gutterBottom={true}
                />
              </Grid>
              <Grid className={classes.dateRangePickerGrid}>
                <Grid>
                  <DateRangePicker
                    startDateInputSettings={customStartDateInputSettings}
                    endDateInputSettings={customEndDateInputSettings}
                    max={maxDateRange}
                    value={dateRangeValue}
                    onChange={dateRangeOnChange}
                  />
                </Grid>
                <Grid>
                  <TextBox
                    value={maskString(logsUploadPath.Path)}
                    readOnly={true}
                    prefix={() => (
                      <>
                        <InputPrefix style={{ backgroundColor: '#EBEBE4' }}>
                          <span className={classes.span}>
                            {GetString('LOG_SERVER')}
                          </span>
                        </InputPrefix>
                      </>
                    )}
                    suffix={() => (
                      <>
                        <InputSuffix>
                          <Button>
                            <CopyToClipboard
                              text={logsUploadPath.Path}
                              onCopy={() => setShowCopyToClipBoard(true)}
                            >
                              <ContentCopyIcon />
                            </CopyToClipboard>
                          </Button>
                        </InputSuffix>
                      </>
                    )}
                    style={{ width: 380, height: 38 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <NodeSelection
            command={'REQUEST_LOGS'}
            commandDropdownlistEnable={false}
            setSendToSiteNumberCommand={setSendToSiteNumberCommand}
            setSendToNodeGroupsCommand={setSendToNodeGroupsCommand}
            setSendToLanesCommand={setSendToLanesCommand}
            buttonTitleKey="REQUEST_LOGS"
            setSendToDivisionCommand={setSendToDivisionCommand}
          />
          <Divider />
          <CommandHistory
            title="LOGS_REQUEST_HISTORY"
            commandName={commandName}
            onStartDateChange={onHistoryStartDateChange}
            onEndDateChange={onHistoryEndDateChange}
            onUseCurrentDateTimeFilterChange={onUseCurrentDateTimeFilterChange}
            dropDownFilterCell={LogsFilterCell}
          />
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {commandSend && (
                <Notification
                  className={classes.send}
                  type={{
                    style: logsUploadPath.Path !== '' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        logsUploadPath.Path !== ''
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : 'COMMAND_SEND_FAILED'
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showCopyToClipBoard && (
                <Notification
                  className={classes.copyToClipBoard}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                >
                  {<StringResourceManager stringKey={'COPY_TO_CLIPBOARD'} />}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default LogsContainer;
